import {
  SuppliesShippingConditionStep,
  SuppliesShippingRateStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import {
  SuppliesShippingConditionKind,
  SuppliesShippingListOperator,
  SuppliesShippingNumberOperator,
  SuppliesShippingRateMode,
} from "@redotech/redo-flows-core-schema-common/common";
import { PartialWithRequiredType } from "..";

export type SuppliesConditionStep =
  PartialWithRequiredType<SuppliesShippingConditionStep>;
export type SuppliesRateStep =
  PartialWithRequiredType<SuppliesShippingRateStep>;

/** The persisted shapes: complete, and discriminated on `kind` / `mode`. */
export type SuppliesCondition = SuppliesShippingConditionStep["condition"];
export type SuppliesRate = SuppliesShippingRateStep["rate"];

type PartialCondition = NonNullable<SuppliesConditionStep["condition"]>;
type PartialRate = NonNullable<SuppliesRateStep["rate"]>;

function listOperatorOr(
  operator: SuppliesShippingListOperator | undefined,
): SuppliesShippingListOperator {
  return operator ?? SuppliesShippingListOperator.ANY_OF;
}

/**
 * Reads a deeply-partial condition back as the discriminated union the editor
 * is written against; undefined means no kind chosen yet.
 */
export function toSuppliesCondition(
  partial: PartialCondition | undefined,
): SuppliesCondition | undefined {
  if (!partial?.kind) {
    return undefined;
  }
  switch (partial.kind) {
    case SuppliesShippingConditionKind.CART_VALUE:
      return {
        kind: partial.kind,
        operator:
          partial.operator ??
          SuppliesShippingNumberOperator.GREATER_THAN_OR_EQUAL,
        amountMinorUnits: partial.amountMinorUnits ?? 0,
        maxAmountMinorUnits: partial.maxAmountMinorUnits,
      };
    case SuppliesShippingConditionKind.FIRST_TIME_CUSTOMER:
    case SuppliesShippingConditionKind.AUTOMATED_REORDER:
      return { kind: partial.kind };
    case SuppliesShippingConditionKind.MERCHANT:
      return {
        kind: partial.kind,
        operator: listOperatorOr(partial.operator),
        accountIds: partial.accountIds ?? [],
      };
    case SuppliesShippingConditionKind.CART_ITEMS:
      return {
        kind: partial.kind,
        operator: listOperatorOr(partial.operator),
        productIds: partial.productIds ?? [],
      };
    case SuppliesShippingConditionKind.PRODUCT_FAMILY_TAGS:
      return {
        kind: partial.kind,
        operator: listOperatorOr(partial.operator),
        tags: partial.tags ?? [],
      };
    case SuppliesShippingConditionKind.DESTINATION_PROVINCE:
      return {
        kind: partial.kind,
        operator: listOperatorOr(partial.operator),
        provinces: partial.provinces ?? [],
      };
    default:
      return undefined;
  }
}

/** Rate counterpart of {@link toSuppliesCondition}. */
export function toSuppliesRate(
  partial: PartialRate | undefined,
): SuppliesRate | undefined {
  if (!partial?.mode) {
    return undefined;
  }
  switch (partial.mode) {
    case SuppliesShippingRateMode.FIXED:
      return {
        mode: partial.mode,
        amountMinorUnits: partial.amountMinorUnits ?? 0,
      };
    case SuppliesShippingRateMode.PERCENT_OF_CART:
      return {
        mode: partial.mode,
        percentBasisPoints: partial.percentBasisPoints ?? 0,
      };
    default:
      return undefined;
  }
}
